import { Component, Input, OnDestroy, OnInit, inject } from "@angular/core";
import { AbstractControl, FormGroup } from "@angular/forms";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { MatDialogRef } from "@angular/material/dialog";
import { Utilities } from "@tylertech/shared-module";
import { Command, LabelCommand } from "@tylertech/commands-module";
import { SystemInfoService } from "@tylertech/core-module";
import { LgdFormControlFactory, setEnabled } from "@tylertech/lgd-reactive-forms";
import { CommonDialogsService } from "@tylertech/common-dialogs-module";
import { ProjectAccountLookupService } from "@tylertech/project-accounting-module";
import
{
    IItemAdjustment,
    IItemLocation,
    IProjectAccount,
    ITransactionGroup,
    InputType,
    ItemViewModel,
    LocationCodeViewModel,
    LookupCriteria,
    TransactionCodeViewModel,
    ExecutionResult,
    ITransaction,
} from "@tylertech/tyler-api-types";
import
{
    ItemAdjustmentService,
    ItemLocationService,
    ItemLookupService,
    LocationCodeLookupService,
    TransactionCodeLookupService,
} from "../../services";
type Item = ItemViewModel & Partial<Record<"$index", number>>;
@Component({
    selector: "add-adjustment",
    templateUrl: "./addAdjustment.tmpl.html",
    styleUrls: ["./addAdjustment.component.scss"],
    standalone: false
})
export class AddAdjustmentComponent implements OnInit, OnDestroy
{
    private dialogRef = inject<MatDialogRef<unknown, IItemAdjustment | undefined>>(MatDialogRef);
    private $commonDialogService = inject(CommonDialogsService);
    private $adjustmentService = inject(ItemAdjustmentService);
    private $inventoryLocationLookupService = inject(LocationCodeLookupService);
    private inventoryItemLookupService = inject(ItemLookupService);
    private $transactionCodeService = inject(TransactionCodeLookupService);
    private $itemLocationService = inject(ItemLocationService);
    private $projectAccountLookupService = inject(ProjectAccountLookupService);
    private $systemInfoService = inject(SystemInfoService);

    private readonly destructor$ = new Subject<void>();
    private readonly itemSubject = new BehaviorSubject<Array<Item>>([]);
    private readonly locationSubject = new BehaviorSubject<Array<LocationCodeViewModel>>([]);
    private readonly projectAccountSubject = new BehaviorSubject<Array<IProjectAccount>>([]);
    private readonly transactionCodeSubject = new BehaviorSubject<Array<TransactionCodeViewModel>>([]);
    readonly commands: Array<Command>;
    readonly addAdjustmentForm: FormGroup;
    /** @deprecated Why was this needed? */ private _projectAccount: IProjectAccount;
    private adjustment: IItemAdjustment;
    filterItems: boolean;
    available: number;
    saveErrorMessage: string;
    processing: boolean;
    isLoaded: boolean;
    @Input()
    itemId: string;

    @Input()
    transaction: ITransactionGroup;

    constructor()
    {
        this.saveErrorMessage = null;
        this.addAdjustmentForm = new FormGroup({
            number: LgdFormControlFactory.text(undefined, true, {}),
            adjustmentDate: LgdFormControlFactory.date(undefined, false, {
                // TODO(ET) - this class didn't have a property `maxDate` but was used in the template....
                // max: () => this.maxDate,
            }),
            description: LgdFormControlFactory.text(undefined, true, {
                maxLength: 50,
            }),
            // TODO(ET) - i suspect `this.filterItems` is more dynamic than we can deal with in the constructor
            item: this.filterItems
                ? LgdFormControlFactory.select<ItemViewModel>(undefined, () => this.filterItems, {
                    displayValueFn: (x: ItemViewModel) =>
                    {
                        return x ? (x.Number ? `${x.Number} - ${x.Description}` : x.Description) : "";
                    },
                })
                : LgdFormControlFactory.typeahead<ItemViewModel>(undefined, () => !this.filterItems, {
                    displayValueFn: (x: ItemViewModel) =>
                    {
                        return x ? (x.Number ? `${x.Number} - ${x.Description}` : x.Description) : "";
                    },
                }),
            location: LgdFormControlFactory.typeahead<LocationCodeViewModel>(undefined, true, {
                displayValueFn: (x: LocationCodeViewModel) =>
                {
                    return x ? `${x.Code} - ${x.Description}` : "";
                },
            }),
            transactionCode: LgdFormControlFactory.select<TransactionCodeViewModel>(
                {
                    disabled: true,
                    value: undefined,
                },
                true,
                {
                    displayValueFn: (x: TransactionCodeViewModel) =>
                    {
                        return x ? `${x.Code} - ${x.Description}` : "";
                    },
                }
            ),
            quantity: LgdFormControlFactory.number(undefined, true, {}),
            price: LgdFormControlFactory.currency(undefined, true, {
                min: 0,
                scale: 6,
            }),
            projectAccount: LgdFormControlFactory.typeahead<IProjectAccount>(
                {
                    disabled: true,
                    value: undefined,
                },
                false,
                {
                    displayValueFn: (x: IProjectAccount) =>
                    {
                        return x ? `${x.Number} - ${x.Name}` : "";
                    },
                }
            ),
        });
        this.commands = [
            new LabelCommand({
                label: "Cancel",
                action: () => this.close(),
                getEnabled: () => true,
            }),
            new LabelCommand({
                label: "Submit",
                action: () => this.save(),
                getEnabled: () => this.addAdjustmentForm.valid && !this.processing,
            }),
        ];
    }

    private get numberControl(): AbstractControl
    {
        return this.addAdjustmentForm.get("number");
    }

    private get adjustmentDateControl(): AbstractControl
    {
        return this.addAdjustmentForm.get("adjustmentDate");
    }

    private get descriptionControl(): AbstractControl
    {
        return this.addAdjustmentForm.get("description");
    }

    private get itemControl(): AbstractControl
    {
        return this.addAdjustmentForm.get("item");
    }

    private get locationControl(): AbstractControl
    {
        return this.addAdjustmentForm.get("location");
    }

    private get transactionCodeControl(): AbstractControl
    {
        return this.addAdjustmentForm.get("transactionCode");
    }

    private get quantityControl(): AbstractControl
    {
        return this.addAdjustmentForm.get("quantity");
    }

    private get priceControl(): AbstractControl
    {
        return this.addAdjustmentForm.get("price");
    }

    private get projectAccountControl(): AbstractControl
    {
        return this.addAdjustmentForm.get("projectAccount");
    }

    get itemsObservable(): Observable<Array<unknown>>
    {
        return this.itemSubject.asObservable();
    }

    get locationsObservable(): Observable<Array<unknown>>
    {
        return this.locationSubject.asObservable();
    }

    get transactionCodesObservable(): Observable<Array<unknown>>
    {
        return this.transactionCodeSubject.asObservable();
    }

    get projectAccountsObservable(): Observable<Array<unknown>>
    {
        return this.projectAccountSubject.asObservable();
    }

    get availableSet(): boolean
    {
        return !Utilities.IsNullOrUndefined(this.available);
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        const self = this;
        this.setup().then(() =>
        {
            this.numberControl.setValue(this.adjustment.Number, { emitEvent: false });
            this.adjustmentDateControl.setValue(this.adjustment.AdjustmentDate, { emitEvent: false });
            this.descriptionControl.setValue(this.adjustment.Description, { emitEvent: false });
            this.itemControl.setValue(this.adjustment.InventoryItem, { emitEvent: false });
            this.locationControl.setValue(this.adjustment.LocationCode, { emitEvent: false });
            this.transactionCodeControl.setValue(this.adjustment.TransactionCode, { emitEvent: false });
            this.quantityControl.setValue(this.adjustment.Quantity, { emitEvent: false });
            this.priceControl.setValue(this.adjustment.Price, { emitEvent: false });
            this.projectAccountControl.setValue(this._projectAccount, { emitEvent: false });
            this.isLoaded = true;
        });
        subscribeToValueChanges(this.numberControl, (newValue: string) =>
        {
            this.adjustment.Number = newValue;
        });
        subscribeToValueChanges(this.adjustmentDateControl, (newValue: Date) =>
        {
            this.adjustment.AdjustmentDate = newValue;
        });
        subscribeToValueChanges(this.descriptionControl, (newValue: string) =>
        {
            this.adjustment.Description = newValue;
        });
        // TODO(ET) the item controls are going to be wonky
        /*
        [
            {
                "name": "Item",
                "type": "AutoComplete",
                "attributes": [
                    "flex=\"100\"",
                    "ng-if=\"!ctrl.filterItems\"",
                    "name=\"Item\"",
                    "aria-label=\"Edit-Item-ItemAdjustment-Inventory\"",
                    "label=\"Item\"",
                    "model=\"ctrl.adjustment.InventoryItem\"",
                    "get-search-text=\"ctrl.parseSearchText(searchText)\"",
                    "on-select=\"ctrl.selectItem(item)\"",
                    "get-template-text=\"ctrl.getItemTemplateText(item)\"",
                    "lookup-service=\"ctrl.$inventoryItemService\"",
                    "error-messages=\"ctrl.errorMessages\"",
                    "required=\"!ctrl.filterItems\"",
                    "is-disabled=\"!ctrl.enableItem\""
                ]
            },
            {
                "name": "Item",
                "type": "Select",
                "attributes": [
                    "name=\"Item\"",
                    "aria-label=\"Selection-Item-ItemAdjustment-Inventory\"",
                    "ng-model=\"ctrl.adjustment.InventoryItem\"",
                    "required=\"ctrl.filterItems\"",
                    "ng-change=\"ctrl.selectItem(ctrl.adjustment.InventoryItem)\""
                ]
            }
        ]
        */
        subscribeToValueChanges(this.itemControl, (newValue: Item) =>
        {
            this.selectItem(newValue);
            this.adjustment.InventoryItem = newValue;
        });
        subscribeToValueChanges(this.locationControl, (newValue: LocationCodeViewModel) =>
        {
            this.selectLocation(newValue);
            this.adjustment.LocationCode = newValue;
        });
        subscribeToValueChanges(this.transactionCodeControl, (newValue: TransactionCodeViewModel) =>
        {
            this.adjustment.TransactionCode = newValue;
        });
        subscribeToValueChanges(this.quantityControl, (newValue: number) =>
        {
            this.adjustment.Quantity = newValue;
        });
        subscribeToValueChanges(this.priceControl, (newValue: number) =>
        {
            this.adjustment.Price = newValue;
        });
        subscribeToValueChanges(this.projectAccountControl, (newValue: IProjectAccount) =>
        {
            this.selectProjectAccount(newValue);
        });
        function subscribeToValueChanges<T>(control: AbstractControl, subscriptionFn: (newValue: T) => void): void
        {
            control.valueChanges.pipe(takeUntil(self.destructor$)).subscribe(x =>
            {
                subscriptionFn(x);
            });
        }
    }

    private setup(): Promise<void>
    {
        /*
        await this.loadLicenses();
        this.filterItems = !Utilities.IsNullOrUndefined(this.transaction);
        const maxDate = new Date();
        maxDate.setHours(0, 0, 0, 0);
        this.adjustment = <IItemAdjustment>{
            AdjustmentDate: maxDate,
            EntityId: null,
            IsNew: true,
            IsDeleted: false,
        };
        if (!Utilities.IsNullOrUndefinedOrEmpty(this.itemId))
        {
            await this.loadItem();
        }
        if (!Utilities.IsNullOrUndefined(this.transaction))
        {
            this.adjustment.EntityId = this.transaction.Id;
            this.setItems();
        }
        await this.generateNewNumber();
        */
        return this.$systemInfoService
            .IsLicensedFor("ProjectAccountingModule")
            .then(isLicensed =>
            {
                setEnabled(this.projectAccountControl, isLicensed, undefined, false);
                this.filterItems = !!this.transaction;
                const maxDate = new Date();
                maxDate.setHours(0, 0, 0, 0);
                this.adjustment = <IItemAdjustment>{
                    AdjustmentDate: maxDate,
                    EntityId: null,
                    IsNew: true,
                    IsDeleted: false,
                };
                if (this.itemId)
                {
                    return this.loadItem();
                }
            })
            .then(() =>
            {
                if (this.transaction)
                {
                    this.adjustment.EntityId = this.transaction.Id;
                    this.updateFilteredItems();
                }
            })
            .then(() =>
            {
                return this.generateNewNumber();
            });
    }

    private loadItem(): Promise<void>
    {
        const criteria: LookupCriteria = {
            Parameters: [
                {
                    Label: "Id",
                    FieldId: "Id",
                    Value: this.itemId,
                    InputType: InputType.Text,
                    IsVisible: false,
                },
            ],
        };
        return this.inventoryItemLookupService.Lookup(criteria).then((x: ExecutionResult<Array<ItemViewModel>>) =>
        {
            const result = x.Result;
            this.adjustment.InventoryItem = result[0];
            setEnabled(this.locationControl, true);
            setEnabled(this.itemControl, false);
        });
    }

    private updateFilteredItems(): void
    {
        this.itemSubject.next(
            this.transaction.Transactions.reduce((list: Array<Item>, tran: ITransaction) =>
            {
                if (!tran.InventoryItem.Id)
                {
                    return list.concat([tran.InventoryItem]);
                }
                if (!list.find(x => x.Id === tran.InventoryItem.Id))
                {
                    return list.concat([tran.InventoryItem]);
                }
                return list;
            }, [])
        );
    }

    private close(): void
    {
        this.dialogRef.close();
    }

    private defaultTransactionItemProperties(item: Item): void
    {
        const transactionItem = this.transaction.Transactions[item.$index];
        if (transactionItem?.ProjectAccount)
        {
            this.selectProjectAccount(transactionItem.ProjectAccount);
        }
    }

    private generateNewNumber(): Promise<void>
    {
        return this.$adjustmentService.GetNextAdjustmentNumber().then(
            (x: string) =>
            {
                this.adjustment.Number = x;
            },
            (_err: unknown) =>
            {
                return this.$commonDialogService.showAlertMessage(
                    "You do not have permission to add Item Adjustments.",
                    "No Permission"
                );
            }
        );
    }

    lookupItems($event: string): void
    {
        this.inventoryItemLookupService
            .Lookup({ SearchText: $event })
            .then((executionResult: ExecutionResult<Array<Item>>) =>
            {
                this.itemSubject.next(
                    executionResult.Result.map((x, i) =>
                    {
                        x.$index = i;
                        return x;
                    })
                );
            });
    }

    lookupLocations($event: string): void
    {
        this.$inventoryLocationLookupService
            .Lookup({
                SearchText: $event,
                Parameters: [
                    {
                        FieldId: "ItemId",
                        Value: this.adjustment?.InventoryItem?.Id,
                    },
                ],
            })
            .then((executionResult: ExecutionResult<Array<LocationCodeViewModel>>) =>
            {
                this.locationSubject.next(executionResult.Result);
            });
    }

    lookupProjectAccounts($event: string): void
    {
        this.$projectAccountLookupService
            .Lookup({ SearchText: $event })
            .then((executionResult: ExecutionResult<Array<IProjectAccount>>) =>
            {
                this.projectAccountSubject.next(executionResult.Result);
            });
    }

    private loadOnHandUnits(item: ItemViewModel, locationId: string): void
    {
        if (!locationId || !item)
        {
            return;
        }
        this.$itemLocationService
            .GetItemLocationCounts({
                LocationId: locationId,
                ItemId: item.Id,
            } as IItemLocation)
            .then(x =>
            {
                this.available = x.OnHand;
            });
    }

    private loadTransactionCodes(locationId: string): void
    {
        if (!locationId)
        {
            return;
        }
        const criteria = <LookupCriteria>{
            Parameters: [
                {
                    Label: "TransactionType",
                    FieldId: "TransactionType",
                    Value: "A",
                    InputType: InputType.Text,
                    IsVisible: false,
                },
                {
                    Label: "LocationCodeId",
                    FieldId: "LocationCodeId",
                    Value: locationId,
                    InputType: InputType.Text,
                    IsVisible: false,
                },
            ],
        };
        this.$transactionCodeService
            .Lookup(criteria)
            .then((executionResult: ExecutionResult<Array<TransactionCodeViewModel>>) =>
            {
                setEnabled(this.transactionCodeControl, true);
                this.transactionCodeSubject.next(executionResult.Result);
                this.adjustment.TransactionCode = executionResult.Result.filter(t => t.IsDefault)[0];
            });
    }

    private save(): void
    {
        this.processing = true;
        this.$adjustmentService
            .SaveAdjustment(this.adjustment)
            .then(
                (result: IItemAdjustment) =>
                {
                    // ! TODO: Review Dialog Conversion; call expression was 'this.$mdDialog.hide(result).'
                    this.dialogRef.close(result);
                },
                (error): void =>
                {
                    const msg = Utilities.getMessage(error, "Failed to save Adjustment");
                    this.saveErrorMessage = msg;
                }
            )
            .finally(() =>
            {
                this.processing = false;
            });
    }

    private selectItem(item: Item): void
    {
        setEnabled(this.locationControl, true);
        if (this.filterItems)
        {
            this.defaultTransactionItemProperties(item);
        }
        this.available = undefined;
        if (this.adjustment.LocationCode?.Id && Utilities.isGuid(this.adjustment.LocationCode.Id))
        {
            this.loadOnHandUnits(item, this.adjustment.LocationCode.Id);
        }
    }

    private selectLocation(location: LocationCodeViewModel): void
    {
        if (
            Utilities.IsNullOrUndefined(location) ||
            Utilities.IsNullOrUndefinedOrEmpty(location.Id) ||
            Utilities.isGuid(location.Id) !== true
        )
        {
            return;
        }
        this.loadTransactionCodes(location.Id);
        this.available = null;
        this.loadOnHandUnits(this.adjustment.InventoryItem, location.Id);
    }

    private selectProjectAccount(account: IProjectAccount): void
    {
        this._projectAccount = account;
        this.adjustment.PAProjectAccountId = account?.Id;
    }
}
